// =============================================================================
// PRACOWNICY ↔ OSOBY Z RECEPTORA — czysta warstwa (bez Supabase, bez Reacta).
//
// Kontrakt: PIN pracownika pobierany z Receptora (migracja 758, agent 0.5.0).
// Ten plik odpowiada na pytania rozstrzygalne bez sieci, żeby test mierzył
// decyzje, a nie atrapę bazy:
//
//   * którzy agenci UMIEJĄ pobrać listę osób i PIN (aktywny, w licencji,
//     wersja ≥ 0.5.0) — a którzy są za starzy i trzeba to powiedzieć;
//   * jak przeczytać listę osób z `wynik` zadania `receptor.odczyt.uzytkownicy`
//     (tolerancyjnie: wpis bez liczbowego `IdUser` wypada, bo nie da się go
//     powiązać);
//   * co powiedzieć o PIN-ie pracownika na podstawie najnowszego zadania
//     `receptor.pin.pobierz` — czeka / przypisany / odrzucony z powodem / błąd.
//
// PIN-u nie ma tu NIGDZIE: lista niesie jego stan, a wynik zadania pin —
// wyłącznie status (`statusPinuZWyniku` nie dotyka pola `pin`).
// =============================================================================

import {
  OPIS_STATUSU_PINU,
  WERSJA_PRACOWNIKOW_RECEPTORA,
  statusPinuZWyniku,
  wersjaAgentaCoNajmniej,
  zadanieWToku,
  type StatusPinu,
  type ZadanieAgenta,
} from './rodzajeZadanReceptora';
import { komunikatCzekania, type AgentDoStanu } from './stanAgentaReceptora';

// ── Agenci zdolni do pracy z osobami ──────────────────────────────────────

/** Podzbiór wiersza agenta potrzebny decyzji — strukturalnie zgodny z `AgentReceptora`. */
export interface AgentDoOsob {
  id: string;
  name: string;
  status: 'pending' | 'active' | 'revoked';
  app_version: string | null;
}

/** Podzbiór stanu licencji — strukturalnie zgodny z `StanLicencjiReceptora`. */
export interface LicencjaDoOsob {
  ok: boolean;
  agenci: Array<{ id: string; w_licencji: boolean }>;
}

export interface AgenciDoOsob<A extends AgentDoOsob> {
  /** Aktywni, w licencji, wersja ≥ 0.5.0 — tylko oni dostają przyciski. */
  zdolni: A[];
  /** Aktywni i w licencji, ale starsi niż 0.5.0 — ekran mówi, że trzeba zaktualizować. */
  zaStarzy: A[];
}

/**
 * Rozdział agentów. Licencja, której nie udało się sprawdzić (`null`/`'blad'`),
 * albo licencja, która nie obowiązuje, daje DWIE PUSTE listy — ekran nie
 * proponuje wtedy niczego, co usługa i tak odrzuci kodem licencji.
 */
export function agenciDoOsob<A extends AgentDoOsob>(
  agenci: A[],
  licencja: LicencjaDoOsob | 'blad' | null
): AgenciDoOsob<A> {
  if (licencja === null || licencja === 'blad' || !licencja.ok) return { zdolni: [], zaStarzy: [] };
  const wLicencji = new Set(
    (Array.isArray(licencja.agenci) ? licencja.agenci : [])
      .filter((a) => a.w_licencji)
      .map((a) => a.id)
  );
  const pracujacy = agenci.filter((a) => a.status === 'active' && wLicencji.has(a.id));
  return {
    zdolni: pracujacy.filter((a) =>
      wersjaAgentaCoNajmniej(a.app_version, WERSJA_PRACOWNIKOW_RECEPTORA)
    ),
    zaStarzy: pracujacy.filter(
      (a) => !wersjaAgentaCoNajmniej(a.app_version, WERSJA_PRACOWNIKOW_RECEPTORA)
    ),
  };
}

/** Jedno zdanie dla agenta starszego niż 0.5.0. */
export function zdanieAgentaZaStarego(agent: Pick<AgentDoOsob, 'app_version'>): string {
  return `Agent Receptora ma wersję ${agent.app_version ?? 'nieznaną'} — PIN-y pobiera wersja ${WERSJA_PRACOWNIKOW_RECEPTORA} lub nowsza`;
}

// ── Lista osób z wyniku odczytu ───────────────────────────────────────────

export type StanPinuOsoby = 'zgodny' | 'niezgodny' | 'brak';

export interface OsobaReceptora {
  id_user: number;
  login: string | null;
  imie: string | null;
  nazwisko: string | null;
  stanowisko: string | null;
  aktywny: boolean;
  stan_pinu: StanPinuOsoby;
}

export interface ListaOsobReceptora {
  osoby: OsobaReceptora[];
  liczba: number;
  obciete: boolean;
  pobrano_at: string | null;
}

const tekstLubNull = (v: unknown): string | null =>
  typeof v === 'string' && v.trim().length > 0 ? v.trim() : null;

/**
 * Wynik `receptor.odczyt.uzytkownicy` → lista osób. `null`, gdy kształt nie
 * jest listą (stary agent, inny rodzaj). Stan PIN-u spoza trzech znanych to
 * `niezgodny`: PIN-u, którego stanu nie rozumiemy, nie obiecujemy pobrać.
 */
export function osobyZWyniku(wynik: unknown): ListaOsobReceptora | null {
  if (typeof wynik !== 'object' || wynik === null) return null;
  const r = wynik as Record<string, unknown>;
  if (!Array.isArray(r.uzytkownicy)) return null;
  const osoby = r.uzytkownicy
    .filter(
      (x): x is Record<string, unknown> =>
        typeof x === 'object' &&
        x !== null &&
        Number.isInteger((x as { id_user?: unknown }).id_user) &&
        ((x as { id_user: number }).id_user as number) > 0
    )
    .map(
      (x): OsobaReceptora => ({
        id_user: x.id_user as number,
        login: tekstLubNull(x.login),
        imie: tekstLubNull(x.imie),
        nazwisko: tekstLubNull(x.nazwisko),
        stanowisko: tekstLubNull(x.stanowisko),
        aktywny: x.aktywny !== false && x.aktywny !== 0,
        stan_pinu: x.stan_pinu === 'zgodny' || x.stan_pinu === 'brak' ? x.stan_pinu : 'niezgodny',
      })
    );
  return {
    osoby,
    liczba: typeof r.liczba === 'number' ? r.liczba : osoby.length,
    obciete: r.obciete === true,
    pobrano_at: typeof r.pobrano_at === 'string' ? r.pobrano_at : null,
  };
}

/** „Nazwisko Imię", a bez nazwiska login, a bez loginu numer. */
export function etykietaOsoby(o: OsobaReceptora): string {
  const nazwa = [o.nazwisko, o.imie].filter((c): c is string => c !== null).join(' ');
  if (nazwa) return nazwa;
  if (o.login) return o.login;
  return `osoba nr ${o.id_user}`;
}

/** Bez wielkości liter i bez ogonków — „lukasz" znajduje „Łukasz". */
function znormalizuj(s: string): string {
  return s.toLocaleLowerCase('pl').replace(/ł/g, 'l').normalize('NFD').replace(/[̀-ͯ]/g, '');
}

/**
 * Wyszukiwanie po imieniu, nazwisku, loginie, stanowisku i numerze. Każde
 * słowo frazy musi pasować do czegoś — „anna piek" znajduje Annę-piekarza.
 */
export function filtrujOsoby(osoby: OsobaReceptora[], fraza: string): OsobaReceptora[] {
  const slowa = znormalizuj(fraza).split(/\s+/).filter(Boolean);
  if (slowa.length === 0) return osoby;
  return osoby.filter((o) => {
    const tekst = znormalizuj(
      [o.imie, o.nazwisko, o.login, o.stanowisko, String(o.id_user)].filter(Boolean).join(' ')
    );
    return slowa.every((s) => tekst.includes(s));
  });
}

/** Krótka etykieta stanu PIN-u przy osobie na liście. */
export const ETYKIETA_STANU_PINU: Record<StanPinuOsoby, string> = {
  zgodny: 'PIN do pobrania',
  niezgodny: 'PIN nie ma 4 cyfr',
  brak: 'bez PIN-u',
};

/**
 * Czy PIN nowego/edytowanego pracownika ma przyjść z Receptora. Tylko osoba
 * z PIN-em DOKŁADNIE 4-cyfrowym i tylko wtedy, gdy administrator nie wybrał
 * „Ustaw PIN ręcznie".
 */
export function pinZReceptora(osoba: OsobaReceptora | null, pinRecznie: boolean): boolean {
  return osoba !== null && osoba.stan_pinu === 'zgodny' && !pinRecznie;
}

/**
 * Dlaczego PIN tej osoby NIE przyjdzie z Receptora — zdanie pod polem PIN.
 * `null` dla PIN-u zgodnego (wtedy pole zastępuje informacja o pobraniu).
 */
export function zdaniePinuOsoby(osoba: OsobaReceptora, edycja: boolean): string | null {
  const skutek = edycja
    ? 'pracownik zachowa obecny PIN, chyba że wpiszesz albo wygenerujesz nowy.'
    : 'pracownik dostanie PIN wygenerowany w Universe.';
  if (osoba.stan_pinu === 'niezgodny')
    return `PIN tej osoby w Receptorze nie ma 4 cyfr — ${skutek}`;
  if (osoba.stan_pinu === 'brak') return `Ta osoba nie ma PIN-u w Receptorze — ${skutek}`;
  return null;
}

// ── Powiązania i stan PIN-u pracowników ───────────────────────────────────

/** Wiersz `shop_floor_worker_external_ids` z `system = 'receptor'` (bez PII). */
export interface WierszPowiazaniaReceptora {
  worker_id: string;
  /** Id agenta (`receptor_agents.id`) — IdUser ma sens tylko w JEGO bazie. */
  company: string;
  external_id: string | null;
  acronym: string;
  last_status: string | null;
  updated_at: string | null;
}

export interface PowiazanieReceptora {
  worker_id: string;
  agent_id: string;
  id_user: number | null;
  updated_at: string | null;
}

export interface StanReceptoraPracownika {
  powiazanie: PowiazanieReceptora;
  /** Najnowsze zadanie pobrania PIN-u dla TEGO powiązania (agent + osoba) albo `null`. */
  zadanie: ZadanieAgenta | null;
}

function idUserZWiersza(w: WierszPowiazaniaReceptora): number | null {
  const surowy = w.external_id ?? w.acronym;
  const liczba = Number(surowy);
  return Number.isInteger(liczba) && liczba > 0 ? liczba : null;
}

/**
 * Powiązania + zadania pin → stan na pracownika. Zadanie liczy się tylko,
 * gdy dotyczy OBECNEGO powiązania (ten sam agent i ta sama osoba) — po zmianie
 * osoby stary „PIN pobrany" opisywałby kogoś innego. Przy kilku wierszach
 * jednego pracownika (ponowne parowanie = nowy agent) wygrywa najświeższy.
 */
export function stanyPracownikowReceptora(
  powiazania: WierszPowiazaniaReceptora[],
  zadania: ZadanieAgenta[]
): Map<string, StanReceptoraPracownika> {
  const najnowsze = new Map<string, WierszPowiazaniaReceptora>();
  for (const w of powiazania) {
    const dotad = najnowsze.get(w.worker_id);
    if (!dotad || (w.updated_at ?? '') > (dotad.updated_at ?? '')) najnowsze.set(w.worker_id, w);
  }

  const wynik = new Map<string, StanReceptoraPracownika>();
  for (const [workerId, w] of najnowsze) {
    const idUser = idUserZWiersza(w);
    let zadanie: ZadanieAgenta | null = null;
    for (const z of zadania) {
      const p = (z.parametry ?? {}) as Record<string, unknown>;
      if (p.worker_id !== workerId) continue;
      if (z.agent_id !== w.company) continue;
      if (idUser === null || Number(p.id_user) !== idUser) continue;
      if (!zadanie || z.created_at > zadanie.created_at) zadanie = z;
    }
    wynik.set(workerId, {
      powiazanie: {
        worker_id: workerId,
        agent_id: w.company,
        id_user: idUser,
        updated_at: w.updated_at,
      },
      zadanie,
    });
  }
  return wynik;
}

/**
 * Rytm odświeżania stanu PIN-ów na kafelkach — tylko dopóki jakieś zadanie czeka.
 * 5 s = puls agenta 0.8.0 (plan 59): odczyt rzadszy niż puls opóźniałby „PIN pobrany”.
 */
export const ODSWIEZANIE_STANU_PINOW_MS = 5_000;

/**
 * Ile czekamy na agenta, zanim powiemy wprost, że nie odpowiada. 3 min obejmuje
 * obie wersje floty: 0.7.x pulsuje co 60 s, 0.8.0 co 5 s (plan 59, S10).
 */
export const LIMIT_CZEKANIA_NA_AGENTA_MS = 3 * 60_000;

export const KOMUNIKAT_BRAK_ODPOWIEDZI =
  'Agent Receptora nie odpowiedział w ciągu 3 minut — sprawdź w Urządzenia › Integracje, czy działa, i spróbuj ponownie';

export interface OpisPinuReceptora {
  stan: 'czeka' | 'nie_odpowiada' | 'przypisany' | 'odrzucony' | 'blad';
  /** Status z wyniku przy `odrzucony`/`przypisany`, inaczej `null`. */
  powod: StatusPinu | null;
  tekst: string;
  ton: 'info' | 'success' | 'warning' | 'error';
}

/**
 * Najnowsze zadanie pin → jedno zdanie o PIN-ie pracownika. `null` = PIN nie
 * był jeszcze pobierany (powiązanie bez pobrania). Zadanie żywe dłużej niż
 * {@link LIMIT_CZEKANIA_NA_AGENTA_MS} nie udaje, że „zaraz będzie".
 */
export function opisPinuZReceptora(
  zadanie: ZadanieAgenta | null,
  teraz: number = Date.now(),
  /** Agent zadania (`zadanie.agent_id`) — zdanie czekania zależy od jego stanu i okresu pulsu. */
  agent: AgentDoStanu | null = null
): OpisPinuReceptora | null {
  if (!zadanie) return null;
  if (zadanieWToku(zadanie)) {
    const od = Date.parse(zadanie.created_at);
    if (Number.isFinite(od) && teraz - od > LIMIT_CZEKANIA_NA_AGENTA_MS) {
      return {
        stan: 'nie_odpowiada',
        powod: null,
        tekst: KOMUNIKAT_BRAK_ODPOWIEDZI,
        ton: 'warning',
      };
    }
    return { stan: 'czeka', powod: null, tekst: komunikatCzekania(agent, teraz), ton: 'info' };
  }
  if (zadanie.status === 'blad') {
    return {
      stan: 'blad',
      powod: null,
      tekst: zadanie.blad?.trim() || 'Agent Receptora nie pobrał PIN-u',
      ton: 'error',
    };
  }
  const status = statusPinuZWyniku(zadanie.wynik) ?? 'nieznany';
  if (status === 'przypisany') {
    return { stan: 'przypisany', powod: status, tekst: OPIS_STATUSU_PINU[status], ton: 'success' };
  }
  return { stan: 'odrzucony', powod: status, tekst: OPIS_STATUSU_PINU[status], ton: 'warning' };
}

/** Czy stan wymaga odpytywania — tylko zadanie żywe i jeszcze w limicie czekania. */
export function pinCzeka(opis: OpisPinuReceptora | null): boolean {
  return opis?.stan === 'czeka';
}

// ── Komunikaty błędów usługi ──────────────────────────────────────────────

/**
 * Zdania na kody odmowy tras pracownika i odczytu osób. Usługa wysyła własny
 * komunikat i ten ma pierwszeństwo (np. `receptor_user_taken` niesie imię
 * i nazwisko tamtego pracownika) — tabela jest dla odpowiedzi bez `message`.
 */
export const KOMUNIKATY_BLEDOW_RECEPTORA: Record<string, string> = {
  agent_za_stary: `Agent Receptora jest za stary — lista osób i PIN-y wymagają wersji ${WERSJA_PRACOWNIKOW_RECEPTORA} lub nowszej. Zaktualizuj agenta na serwerze.`,
  agent_not_active:
    'Agent Receptora nie jest aktywny — sprawdź go w Urządzenia › Integracje albo wybierz osobę z Receptora ponownie.',
  agent_required: 'Wybierz agenta Receptora, przez którego mam czytać osoby.',
  receptor_user_taken: 'Ta osoba z Receptora jest już powiązana z innym pracownikiem.',
  worker_not_found: 'Tego pracownika nie ma już w kartotece — odśwież stronę.',
  receptor_link_not_found:
    'Pracownik nie jest powiązany z osobą z Receptora — wybierz ją w edycji pracownika.',
  forbidden: 'Powiązanie z Receptorem może zmieniać właściciel albo administrator organizacji.',
};

const KOMUNIKAT_NIEZNANY = 'Nie udało się połączyć z Receptorem — spróbuj ponownie za chwilę.';

/** Ogólnik, który `wolaj` wstawia, gdy ciało odpowiedzi nie miało `message`. */
const OGOLNIK_HTTP = /^Błąd organu Receptora \(HTTP \d+\)$/;

/**
 * Błąd z warstwy API → zdanie dla człowieka. Komunikat usługi przechodzi
 * dosłownie; tabela kodów wchodzi tylko za ogólnik „HTTP 409".
 */
export function komunikatBleduReceptora(err: unknown): string {
  if (typeof err === 'object' && err !== null && 'kod' in err) {
    const { kod, message } = err as { kod: unknown; message?: unknown };
    const tekst = typeof message === 'string' ? message.trim() : '';
    if (tekst && !OGOLNIK_HTTP.test(tekst)) return tekst;
    if (typeof kod === 'string' && KOMUNIKATY_BLEDOW_RECEPTORA[kod]) {
      return KOMUNIKATY_BLEDOW_RECEPTORA[kod];
    }
    return tekst || KOMUNIKAT_NIEZNANY;
  }
  return KOMUNIKAT_NIEZNANY;
}
